// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { useEffect, useRef } from "react";
import { useAppLocale } from "../../context/AppLocaleContext";
import { useAuthSession } from "../../hooks/useAuthSession";
import { isDevPreviewMode } from "../../utils/devEnv";
import type { AppLocale } from "../../i18n/appLocale";
import {
  onLocaleChanged,
  onLocaleHydrated,
  onLocaleWriteFailed,
  type LocaleSyncState,
} from "../../i18n/appLocale";
import { readLocalePreference, writeLocalePreference } from "./localePreferenceApi";

export function useLocaleAccountSync(): void {
  const { getAccessToken, isAuthenticated } = useAuthSession();
  const { locale, setLocale } = useAppLocale();
  const stateRef = useRef<LocaleSyncState>({
    hydrated: false,
    dirty: false,
    lastSynced: null,
  });
  const localeRef = useRef(locale);
  const setLocaleRef = useRef(setLocale);
  const skipChange = useRef(true);
  localeRef.current = locale;
  setLocaleRef.current = setLocale;

  useEffect(() => {
    if (!isAuthenticated || isDevPreviewMode) {
      stateRef.current = {
        hydrated: true,
        dirty: false,
        lastSynced: localeRef.current,
      };
      return;
    }

    let cancelled = false;
    void (async () => {
      let remote: AppLocale | null = null;
      try {
        const token = await getAccessToken();
        if (token.length > 0) {
          remote = await readLocalePreference({ token });
        }
      } catch {
        remote = null;
      }
      if (cancelled) {
        return;
      }
      const decision = onLocaleHydrated(stateRef.current, localeRef.current, remote);
      stateRef.current = decision.state;
      if (decision.apply !== null) {
        setLocaleRef.current(decision.apply);
      }
      if (decision.write === null) {
        return;
      }
      try {
        const token = await getAccessToken();
        if (token.length === 0) {
          stateRef.current = onLocaleWriteFailed(stateRef.current);
          return;
        }
        await writeLocalePreference({ token }, decision.write);
      } catch {
        stateRef.current = onLocaleWriteFailed(stateRef.current);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getAccessToken, isAuthenticated]);

  useEffect(() => {
    if (skipChange.current) {
      skipChange.current = false;
      return;
    }
    const decision = onLocaleChanged(stateRef.current, locale);
    stateRef.current = decision.state;
    if (decision.write === null || !isAuthenticated || isDevPreviewMode) {
      return;
    }
    const next = decision.write;
    void (async () => {
      try {
        const token = await getAccessToken();
        if (token.length === 0) {
          stateRef.current = onLocaleWriteFailed(stateRef.current);
          return;
        }
        await writeLocalePreference({ token }, next);
      } catch {
        stateRef.current = onLocaleWriteFailed(stateRef.current);
      }
    })();
  }, [getAccessToken, isAuthenticated, locale]);
}
